import { useEffect, useReducer, useRef, useState } from "react";
import { FirmaElectronicaReducer } from "components/componentesGenerales/firmaElectronica/context/firmaElectronicaReducer";
import {
  CertificadoInterface,
  CertificateType,
  DigestionInterface,
  ExtraParamsInterface,
  initialStateInterface,
  Inputs,
  SIGNPKCS1Interface,
  ValidateStatus,
} from "components/componentesGenerales/firmaElectronica/interfaces/firmaInterfaces";
import { firmaElectronicaTypes } from "components/componentesGenerales/firmaElectronica/types/firmaElectronicaTypes";
import { otherScripts, satLibs } from "static/_firmaElectronicaData";
import {
  digest,
  encoding,
  firelKey,
  getCertificateAttributeFromSubject,
  getFechaFirma,
  getFechasCertificado,
  hexToBase64,
  rstrtohex,
  signPKCS1,
} from "utils/_firmaElectronica";
import moment from "moment";
import { useDebounce } from "./useDebounce";
import { AxiosResponse } from "axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { usePutMarcarSolicitudFirmadaMutation } from "services/solicitudFirmaService/solicitudFirmaService";
import { DataDetalleFirma } from "interfaces/solicitudFirma/solicitudFirmaInterface";

export const useFirmaElectronica = (
  idsReferencias: number[],
  cveTipoDocumentoFirma: number,
  extraParams: ExtraParamsInterface,
  enlaceInvitado: boolean,
  solicitudFirma:DataDetalleFirma,
  derService: (body: any) => Promise<AxiosResponse<any, any>>,
  vectorService: (body: any) => Promise<AxiosResponse<any, any>>,
  firmaArchivosExtendidosService: (
    body: any
  ) => Promise<AxiosResponse<any, any>>,
  validarStutusSignService: (body: any) => Promise<AxiosResponse<any, any>>,
  terminarFirmaAnticipadaService: (
    body: any
  ) => Promise<AxiosResponse<any, any>>,
  obtenerStatusFirmaTerminada?: (
    dataFirmaAnticipada: any,
    statusFima: ValidateStatus
  ) => void,
  onChange?: (statusFima: ValidateStatus) => void
) => {  
  const navigate = useNavigate();
  const isControlled = useRef(!!onChange);
  const initialState: initialStateInterface = {
    /**certificados seleccionados */
    cerSelected: null,
    keySelected: null,
    digestiones: [],
    statusFirma: null,
    listFirmantes: null,
    errorMessage: [],
    derResData: [],
    vectorResData: [],
    iteracionesEncontradas: [],
    algoritmoDigestion: null,
    strCerPem: null,
    strCertificate: "",
    strPrivateKey: "",
    oPrivateKey: "",
    tipo_firmante: null,
    certFile: null,
    keyFile: null,
    password: "",
    showPassword: false,
    loadingFirma: false,
    showCertificadosGuardados: false,
    successFirmado: true,
    stepsFirmas: {
      paso1: false,
      paso2: false,
      paso3: false,
      paso4: false,
      paso5: false,
      paso6: false,
      paso7: false,
    },
  };

  const [state, dispatch] = useReducer(FirmaElectronicaReducer, initialState);
  const [putMarcarSolicitudFirmada] = usePutMarcarSolicitudFirmadaMutation();


  const [successMessage, setSuccessMessage] =
    useState<string>("Iniciando Firmado");

  const debounceSuccessMessage = useDebounce(successMessage, 1000);

  const handleChangeSteps = (step: { [x: string]: any }) => {
    dispatch({
      type: firmaElectronicaTypes.change_step_firma,
      payload: step,
    });
  };

  const handleAddCertSelected = (cer: { [x: string]: any }) => {
    dispatch({
      type: firmaElectronicaTypes.cer_selected,
      payload: cer,
    });
  };

  const handleAddKeySelected = (key: { [x: string]: any }) => {
    dispatch({
      type: firmaElectronicaTypes.key_selected,
      payload: key,
    });
  };

  const handleClickShowPassword = () => {
    dispatch({
      type: firmaElectronicaTypes.show_password,
    });
  };

  /**
   *
   * empieza guardado de certificados
   *
   */
  const addCerFile = (file: File) => {
    dispatch({
      type: firmaElectronicaTypes.add_cert_file,
      payload: file,
    });
  };

  /**
   *
   * @param cer (typo File)
   *
   * Función para leer el certificado
   */
  const readCertificate = (cer: File) => {
    let file: FileReader = new FileReader();
    file.onload = function () {
      let bytes = new Uint8Array(file.result as ArrayBuffer);
      let binary = "";
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
      }

      /** convertir bytes archivo a base64 cer */
      const certHex = rstrtohex(binary);

      /** Guardar base 64 en el state strCertificate */
      addStrCertificate(
        (window as any).forge.util.encode64(
          (window as any).forge.util.hexToBytes(certHex)
        )
      );

      /**evalua si el tamaño del archivo es el mismo que el tamaño de los bytes */
      if (cer.size !== bytes.length) {
        addErrorMessages(["No se ha podido leer el Certificado por completo"]);
        addStrCertificate("");
        startLoadingFirma(false);
        resetStateErrorFirmaElectronica();
        return;
      }

      /**retorna el contenido del certificado */
      addStrCerPem(
        (window as any).KJUR.asn1.ASN1Util.getPEMStringFromHex(
          certHex,
          "CERTIFICATE"
        )
      );
    };
    /**retorna un error si falla la lectura del certificado */
    file.onerror = function (e: any) {
      addErrorMessages(["No se ha podido leer el Certificado por completo"]);
      startLoadingFirma(false);
      resetStateErrorFirmaElectronica();
    };

    file.readAsArrayBuffer(cer);
  };

  const addKeyFile = (file: File) => {
    dispatch({
      type: firmaElectronicaTypes.add_key_file,
      payload: file,
    });
  };

  /**
   *
   *  @param key (type File)
   * Funcion para leer la llave privada
   *
   * */
  const readPrivateKey = (key: File) => {
    let file: FileReader = new FileReader();
    file.onload = function () {
      /**obtenemos la llave como base 64 */
      const base64Key = (file.result as string).split("base64,")[1];
      if (base64Key) {
        addStrPrivateKey(base64Key);
      } else {
        addErrorMessages(["No se ha podido leer la llave privada"]);
        startLoadingFirma(false);
        resetStateErrorFirmaElectronica();
      }
    };
    /**retorna un error si falla la lectura del certificado */
    file.onerror = function (e: any) {
      addErrorMessages(["No se ha podido leer el Certificado por completo"]);
      startLoadingFirma(false);
      resetStateErrorFirmaElectronica();
    };
    file.readAsDataURL(key);
  };
  /**
   *
   * termina guardado de certificados
   *
   */

  const addTipoFirmante = (idTipoFirmante: number) => {
    dispatch({
      type: firmaElectronicaTypes.add_tipo_firmante,
      payload: idTipoFirmante,
    });
  };

  const startLoadingFirma = (status: boolean) => {
    dispatch({
      type: firmaElectronicaTypes.loading_firma,
      payload: status,
    });
  };

  const addPassword = (password: string) => {
    dispatch({
      type: firmaElectronicaTypes.add_password,
      payload: password,
    });
  };

  const resetStateErrorFirmaElectronica = () => {
    dispatch({
      type: firmaElectronicaTypes.reset_state_error,
    });
  };

  const resetStateSuccessFirmaElectronica = () => {
    dispatch({
      type: firmaElectronicaTypes.reset_state_success,
    });
  };

  const addErrorMessages = (arrayMessages: string[]) => {
    dispatch({
      type: firmaElectronicaTypes.add_error_messages,
      payload: arrayMessages,
    });
  };

  const addStrCertificate = (strCertificate: string) => {
    dispatch({
      type: firmaElectronicaTypes.add_str_certificate,
      payload: strCertificate,
    });
  };

  const addDigestiones = (digestiones: DigestionInterface[]) => {
    dispatch({
      type: firmaElectronicaTypes.add_digestiones,
      payload: digestiones,
    });
  };

  const addStrCerPem = (strCerPem: string) => {
    dispatch({
      type: firmaElectronicaTypes.add_str_cert_pem,
      payload: strCerPem,
    });
  };

  const addStrPrivateKey = (strPrivateKey: string) => {
    dispatch({
      type: firmaElectronicaTypes.add_str_private_key,
      payload: strPrivateKey,
    });
  };

  const addOPrivateKey = (oPrivateKey: string) => {
    dispatch({
      type: firmaElectronicaTypes.add_o_private_key,
      payload: oPrivateKey,
    });
  };

  const addStatusFirma = (statusFirma: ValidateStatus | null) => {
    dispatch({
      type: firmaElectronicaTypes.add_status_firma,
      payload: statusFirma,
    });
  };

  const addDerResponseData = (der: any) => {
    dispatch({
      type: firmaElectronicaTypes.add_der_response_data,
      payload: der,
    });
  };

  const addVectorReponseData = (vector: any) => {
    dispatch({
      type: firmaElectronicaTypes.add_vector_response_data,
      payload: vector,
    });
  };

  const addAlgoritmoDigestion = (algoritmoDigestion: number) => {
    dispatch({
      type: firmaElectronicaTypes.add_algoritmo_digestion,
      payload: algoritmoDigestion,
    });
  };

  const addIteracionesEncontradas = (iteraciones: any) => {
    dispatch({
      type: firmaElectronicaTypes.add_iteraciones_encontradas,
      payload: iteraciones,
    });
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const handleClickShowCerKeySave = (status: boolean) => {
    dispatch({
      type: firmaElectronicaTypes.show_cer_key_save,
      payload: status,
    });
  };

  const startFinishFirmaAnticipada = () => {
    setSuccessMessage("Terminando Firmado");
    setTimeout(() => {
      startLoadingFirma(true);
    }, 500);
    terminarFirmaAnticipadaService({
      idsReferencias,
      cveTipoDocumentoFirma,
    })
      .then(({ data: dataFirmaAnticipada }: any) => {
        startLoadingFirma(false);
        setSuccessMessage("");
        validarStutusSignService({
          idsReferencias,
          cveTipoDocumentoFirma,
          registrarDigestiones: false,
        })
          .then(({ data: dataStatusFirma }) => {
            obtenerStatusFirmaTerminada &&
            obtenerStatusFirmaTerminada(dataFirmaAnticipada, dataStatusFirma);
          })
          .catch((err) => {
            console.log(err);
          });
      })
      .catch((err) => {
        console.log(err);
        startLoadingFirma(false);
        setSuccessMessage("")
        Swal.fire({
          title: "Advertencia",
          text:
            err.response?.data?.data ||
            "Hubo un problema al finalizar el firmado",
          icon: "warning",
        });
      });
  };

  const isSuccessFirmado = (status: boolean) => {
    dispatch({
      type: firmaElectronicaTypes.is_success_firmado,
      payload: status,
    });
  };

  const saveCertificado = () => {
    Swal.fire({
      title: "¿Deseas guardar el certificado usado?",
      text: "El certificado será guardado para posteriores firmas de documentos",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Si, Guardar",
      cancelButtonText: "No, Finalizar",
    }).then((result: any) => {
      if (result.isConfirmed) {
        const certificadosGuardados = JSON.parse(
          localStorage.getItem("certKeyList") || "[]"
        );

        try {
          localStorage.setItem(
            "certKeyList",
            JSON.stringify([
              ...certificadosGuardados,
              {
                cert: state.cerSelected,
                key: state.keySelected,
              },
            ])
          );

          Swal.fire({
            title: "Correcto",
            text: "Certificado Guardado Correctamente",
            icon: "success",
          });
        } catch (error) {
          Swal.fire({
            title: "Error",
            text: "No se pueden guardar más certificados , límite permitido excedido",
            icon: "success",
          });
        }
      }
    });
  };

  const addListFirmantes = (listFirmantes: ValidateStatus) => {
    dispatch({
      type: firmaElectronicaTypes.add_list_firmantes,
      payload: listFirmantes,
    });
  };

  /**Carga scripts firma electronica */
  const addScript = async (script: any) => {
    return new Promise((resolve, reject) => {
      const scriptElement = document.createElement("script");
      scriptElement.src = script;
      scriptElement.async = false;
      scriptElement.defer = true;
      scriptElement.onload = resolve;
      scriptElement.onerror = reject;
      document.body.appendChild(scriptElement);
    });
  };

  const cargarScripts = async () => {
    const scriptsToWait = [];

    for (let source of satLibs) {
      scriptsToWait.push(
        addScript(
          `${process.env.REACT_APP_URL_PUBLIC}/assets/js/scriptsFirma/${source}`
        )
      );
    }

    for (let source of otherScripts) {
      scriptsToWait.push(
        addScript(
          `${process.env.REACT_APP_URL_PUBLIC}/assets/js/scriptsFirma/forge/${source}`
        )
      );
    }

    await Promise.all(scriptsToWait).then(() => {
      handleChangeSteps({
        ...state.stepsFirmas,
        paso1: true,
      });
    });
  };

  /** fin carga scripts firma electronica */

  /**empieza proceso firmado */
  const initialFirmado = async (data: Inputs) => {
    setSuccessMessage("Iniciando Firmado");
    startLoadingFirma(true);
    isSuccessFirmado(false);
    addErrorMessages([]);
    if (!data.cer) {
      Swal.fire({
        text: "No has seleccionado ningún certificado",
        icon: "warning",
      });
      startLoadingFirma(false);
      return;
    }
    if (!data.key) {
      Swal.fire({
        text: "No has seleccionado ninguna llave privada ",
        icon: "warning",
      });
      startLoadingFirma(false);
      return;
    }
    addPassword(data.password);

    /**inicia validar status firma */
    validarStutusSignService({
      idsReferencias,
      cveTipoDocumentoFirma,
      registrarDigestiones: true,
    })
      .then(({ data }) => {
        addStatusFirma(data);
      })
      .catch((err) => {
        console.log(err);
        addErrorMessages([
          "No se pudo registrar las digestiones de los documentos",
        ]);
        resetStateErrorFirmaElectronica();
      });
  };

  const startFirmado = () => {
    if (!state.statusFirma?.missing.length) {
      addErrorMessages(["No hay referencias que firmar"]);
      startLoadingFirma(false);
      resetStateErrorFirmaElectronica();
      return;
    }
    startDigestionReferences();
  };

  const getDataVector = () => {
    const dataVector: any = state.derResData.map((data: any) => {
      const resp: SIGNPKCS1Interface = signPKCS1(
        state.oPrivateKey,
        data.digestion,
        state.algoritmoDigestion,
        encoding.B64
      );

      if (resp.status === "success") {
        let auxVec = {
          vector: data.digestion.replace("=", ""),
          firma: resp.data?.replace("==", ""),
          cert: state.strCertificate,
          idDocumento: data.idDocumento,
          referencia: data.idReferencia,
          extraParameters: {
            cveAdscripcion: extraParams.cveAdscripcion,
            cveTipoDocumentoFirma: extraParams.cveTipoDocumentoFirma,
            cveGrupo: extraParams.cveGrupo,
            validCurp: 0,
          },
        };
        return auxVec;
      } else {
        let auxVec = {
          vector: "",
          firma: "",
          cert: "",
          idDocumento: data.idDocumento,
          referencia: data.idReferencia,
          extraParameters: {
            cveAdscripcion: extraParams.cveAdscripcion,
            cveTipoDocumentoFirma: extraParams.cveTipoDocumentoFirma,
            cveGrupo: extraParams.cveGrupo,
            validCurp: 0,
          },
        };
        return auxVec;
      }
    });
    return dataVector;
  };

  const startGetVector = () => {
    setSuccessMessage("Creando Vector");

    const dataVector = getDataVector();

    if (dataVector.length === state.derResData.length) {
      const vectorData = {
        debugg: extraParams.debugg,
        cveAdscripcion: extraParams.cveAdscripcion,
        vector: dataVector,
      };

      vectorService(vectorData)
        .then(({ data }) => {
          if (data.data.success.length) {
            addVectorReponseData(data.data.success);
            handleChangeSteps({
              ...state.stepsFirmas,
              paso6: true,
            });
          }
        })
        .catch((err) => {
          console.log(err);
          Swal.fire({
            title: "Advertencia",
            text: "No se pudo generar el vector de los certificados",
            icon: "warning",
          });
          resetStateErrorFirmaElectronica();
        });
    }
  };

  const iniciaEncontrarIteracion = () => {
    const dataVector = getDataVector();
    const responseVec = state.vectorResData.map((v: any) => {
      let newDataVector;
      dataVector.forEach((data: any) => {
        if (
          data.idDocumento === v.idDocumento &&
          data.idReferencia === v.referencia
        ) {
          setSuccessMessage("Se encontro una iteracion");
          newDataVector = {
            Error: v.data.Error,
            Descripcion: v.data.Descripcion,
            Id: v.data.Id,
            Huella: v.data.Huella,
            HexSerie: v.data.HexSerie,
            Fecha: v.data.Fecha,
            Cn: v.data.Cn,
            idReferencia: data.referencia,
            idDocumentoFirmado: data.idDocumento,
            sign: data.firma,
            digestion: data.vector,
          };
        }
      });

      return newDataVector;
    });

    if (state.iteracionesEncontradas.includes(undefined)) {
      addErrorMessages([
        "No se ha podido completar la firma , recarga la página y vuelve a intentarlo",
      ]);
      resetStateSuccessFirmaElectronica();
      return;
    }

    if (!responseVec.includes(undefined)) {
      handleChangeSteps({
        ...state.stepsFirmas,
        paso7: true,
      });
      addIteracionesEncontradas(responseVec);
    }
  };

  const startGetFirmaArchivoExtendidos = () => {
    setSuccessMessage("Terminando el Firmado de documentos");
    const curp = getCertificateAttributeFromSubject(
      state.strCertificate,
      "2.5.4.5"
    );
    const strCN = getCertificateAttributeFromSubject(
      state.strCertificate,
      "2.5.4.3"
    );

    const newDataVector = state.iteracionesEncontradas.map(
      (registroFirma: any) => {
        let docReferencia: any = [];

        state.statusFirma?.missing.forEach((docRef: any) => {
          const res = docRef.data.filter((documentoReferencia: any) => {
            return (
              documentoReferencia.idDocumentosFirmados ===
              registroFirma.idDocumentoFirmado
            );
          });

          docReferencia.push(...res);
        });

        registroFirma.singleName = docReferencia[0]?.singleName;
        return registroFirma;
      }
    );

    const firmaData = {
      debugg: extraParams.debugg,
      cveAdscripcion: extraParams.cveAdscripcion,
      cert: state.strCertificate,
      serie: state.strCertificate,
      cn: strCN,
      curpFirmante: curp,
      jsonData: newDataVector,
      cveTipoFirmante: state.tipo_firmante,
      extraParams: {
        cveAdscripcion: extraParams.cveAdscripcion,
        cveTipoDocumentoFirma: extraParams.cveTipoDocumentoFirma,
        cveGrupo: extraParams.cveGrupo,
        validCurp: 0,
      },
      cveGrupo: extraParams.cveGrupo,
    };

    /***service firma-archivos-extendido */
    firmaArchivosExtendidosService(firmaData)
      .then(({ data }) => {
        if (data.data.success.length) {
          handleChangeSteps({
            ...state.stepsFirmas,
            paso2: false,
            paso3: false,
            paso4: false,
            paso5: false,
            paso6: false,
            paso7: false,
          });
          Swal.fire({
            title: "Correcto",
            text: data.message,
            icon: "success",
          }).then((result: any) => {
            if (result.isConfirmed) {
              if (enlaceInvitado && solicitudFirma) {
                putMarcarSolicitudFirmada({
                  idSolicitudRemota: [solicitudFirma?.idSolicitudRemota],
                })
                  .unwrap()
                  .then(() => {
                    navigate("/auth/login", { replace: true });
                  })
                  .catch((err) => {
                    console.log(err);
                  });
                return;
              }
              const certificadosGuardados = JSON.parse(
                localStorage.getItem("certKeyList") || "[]"
              );
              const existCertificado = certificadosGuardados.find(
                (cert: CertificadoInterface) =>
                  cert.cert.name === state.certFile.name
              );

              if (existCertificado) return;
              saveCertificado();
            }
          });
          resetStateErrorFirmaElectronica();
          setSuccessMessage("");
          isSuccessFirmado(true);
        } else {
          const errors = data.data.error.map((err: any) => err.message);
          if (
            errors.includes(
              "No es posible firmar porque usted ya firmo el documento."
            )
          ) {
            validarStutusSignService({
              idsReferencias,
              cveTipoDocumentoFirma,
              registrarDigestiones: false,
            })
              .then(({ data }) => {
                addListFirmantes(data);
              })
              .catch((err) => {
                console.log(err);
              });
          }
          addErrorMessages(errors);
          addStrCertificate("");
          startLoadingFirma(false);
          setSuccessMessage("");
          resetStateErrorFirmaElectronica();
        }
      })
      .catch((err) => {
        Swal.fire({
          title: "Error",
          text: "Ocurrio un problema al firmar archivos extendidos",
          icon: "error",
        });
        startLoadingFirma(false);
        console.log(err);
      });
  };

  const validateKeyPairs = (password: string) => {
    if (state.strCertificate === "") {
      addErrorMessages(["No se ha podido leer el Certificado por completo"]);
      startLoadingFirma(false);
      resetStateErrorFirmaElectronica();
      return;
    }

    if (state.strPrivateKey === "") {
      addErrorMessages(["No se ha cargado la llave privada"]);
      startLoadingFirma(false);
      resetStateErrorFirmaElectronica();
      return;
    }

    if (!password) {
      addErrorMessages(["Contraseña no ingresada"]);
      startLoadingFirma(false);
      return;
    }

    let certificate: CertificateType = new (window as any).X509();
    certificate.readCertPEM(state.strCerPem);
    let certModule = certificate.subjectPublicKeyRSA.n;

    try {
      const privateKeyDecoded = (window as any).ASN1.decode(
        (window as any).Base64.unarmor(state.strPrivateKey)
      );
      const privateKeyHex = (window as any).obtieneLlavePrivada(
        privateKeyDecoded.toHexString(),
        password
      );
      const privateKeyB64 = hexToBase64(privateKeyHex);
      const rsakey = (window as any).getKeyFromPlainPrivatePKCS8Hex(
        privateKeyHex
      );
      const moduloPrivada = rsakey.n;
      verifyPrivateKey(moduloPrivada, certModule, privateKeyB64, certificate);
    } catch (error) {
      if (error === "PKCS8 private key(code:001)") {
        addErrorMessages([
          "La contraseña para acceder a la llave privada no es correcta, intente de nuevo.",
        ]);
        startLoadingFirma(false);
        resetStateErrorFirmaElectronica();
        return;
      } else if (error === "this only supports pkcs5PBES2") {
        try {
          privateKeyDecoded(certModule, certificate, password);
        } catch (error) {
          addErrorMessages([typeof error === "string" ? error : ""]);
          startLoadingFirma(false);
          resetStateErrorFirmaElectronica();
          return;
        }
        return;
      } else {
        addErrorMessages([typeof error === "string" ? error : ""]);
        resetStateErrorFirmaElectronica();
        return;
      }
    }
  };

  const privateKeyDecoded = (
    certModule: Object,
    certificate: CertificateType,
    password: string
  ) => {
    const privateKeyDecoded = (window as any).ASN1.decode(
      (window as any).Base64.unarmor(state.strPrivateKey)
    );

    const description = firelKey(
      privateKeyDecoded.toHexString(),
      certModule,
      hexToBase64(certificate.hex),
      password,
      addOPrivateKey,
      addStrCertificate
    );

    if (description.length === 2) {
      setSuccessMessage(description![1].toString());
      handleChangeSteps({
        ...state.stepsFirmas,
        paso3: true,
      });
      return;
    }
    resetStateErrorFirmaElectronica();
    setSuccessMessage("");
    addErrorMessages([description.toString()]);
  };

  const verifyPrivateKey = (
    moduloPrivada: any,
    certModule: any,
    privateKeyB64: any,
    certificate: any
  ) => {
    if (
      typeof moduloPrivada !== "undefined" &&
      certModule.equals(moduloPrivada)
    ) {
      addOPrivateKey(privateKeyB64);
      addStrCertificate(hexToBase64(certificate.hex) || "");
      setSuccessMessage(
        "La verificación del par de llaves ha sido satisfactoria"
      );
      handleChangeSteps({
        ...state.stepsFirmas,
        paso3: true,
      });
    } else {
      addErrorMessages([
        "El certificado no corresponde con la llave privada proporcionada",
      ]);
      resetStateErrorFirmaElectronica();
    }
  };

  const compareDateCertificate = async () => {
    const dateCertificate = await getFechasCertificado(state.strCertificate);

    const datenow = new Date().getTime();

    if (dateCertificate?.fechaFinal! < datenow) {
      const dateFormat = moment(dateCertificate?.fechaFinal).format(
        "DD/MM/YYYY"
      );
      addErrorMessages([`El certificado venció el: ${dateFormat}`]);
      startLoadingFirma(false);
      resetStateErrorFirmaElectronica();
      return;
    }

    if (dateCertificate.error) {
      addErrorMessages([dateCertificate.error]);
      startLoadingFirma(false);
      resetStateErrorFirmaElectronica();
      return;
    }

    handleChangeSteps({
      ...state.stepsFirmas,
      paso2: true,
    });
  };

  const startDigestionReferences = () => {
    if (!state.statusFirma?.missing.length) {
      addErrorMessages(["Error al obtener las el estatus de las referencias"]);
      return;
    }

    let docs: any[] = [];
    state.statusFirma.missing.forEach((referencia: any) => {
      const listDigestiones = referencia.data.map((data: any) => {
        let body = {
          documento: data?.singleName,
          digestion: data?.digestion,
          idDocumentosFirmados: data?.idDocumentosFirmados,
          idReferencia: data?.idReferencia,
        };
        return body;
      });
      docs = [...docs, ...listDigestiones];
    });

    handleChangeSteps({
      ...state.stepsFirmas,
      paso4: true,
    });
    addDigestiones(docs);
  };

  const startDigestionFiles = () => {
    if (state.oPrivateKey === "") {
      addErrorMessages(["No se ha cargado elemento para firma electrónica"]);
      startLoadingFirma(false);
      resetStateErrorFirmaElectronica();
      return;
    }

    if (state.strCertificate === "") {
      addErrorMessages(["No se ha cargado elemento para firma electrónica"]);
      startLoadingFirma(false);
      resetStateErrorFirmaElectronica();
      return;
    }

    if (state.digestiones.length) {
      const auxForVector = state.digestiones.map(
        (digestion: DigestionInterface) => {
          let strDate = getFechaFirma();
          let length = (window as any).forge.util.decode64(
            digestion.digestion
          ).length;

          let digesFunction: any = {
            16: digest.MD5,
            20: digest.SHA1,
            32: digest.SHA2,
          };
          addAlgoritmoDigestion(digesFunction[length]);

          let auxVec = {
            digestionDocumento: digestion.digestion.replace("=", ""),
            fecha: strDate,
            idDocumentosFirmados: digestion.idDocumentosFirmados,
            idReferencia: digestion.idReferencia,
          };

          return auxVec;
        }
      );

      getVectorFilev2(auxForVector);
    }
  };

  const getVectorFilev2 = (auxForVector: any) => {
    if (!state.oPrivateKey) {
      addErrorMessages(["No existe una llave privada para realizar la firma"]);
      startLoadingFirma(false);
      resetStateErrorFirmaElectronica();
      return;
    }

    /** body request der */
    const derData = {
      debugg: extraParams.debugg,
      cveAdscripcion: extraParams.cveAdscripcion,
      arraydigest: auxForVector,
    };

    // Una vez creado el arreglo con las digestiones, pasamos todo a el metodo der
    derService(derData)
      .then(({ data }) => {
        if (data.data.success.length) {
          addDerResponseData(data.data.success);
          handleChangeSteps({
            ...state.stepsFirmas,
            paso5: true,
          });
        }
      })
      .catch((err) => {
        Swal.fire({
          title: "Advertencia",
          text: "No se pudo completar la solicitud",
          icon: "warning",
        });
        resetStateErrorFirmaElectronica();
        console.log(err);
      });
  };

  useEffect(() => {
    if (state.successFirmado) {
      validarStutusSignService({
        idsReferencias,
        cveTipoDocumentoFirma,
        registrarDigestiones: false,
      })
        .then(({ data }) => {
          addListFirmantes(data);
        })
        .catch((err) => {
          console.log(err);
        });
      addErrorMessages([]);
    }
    return () => {
      resetStateSuccessFirmaElectronica();
    };
    // eslint-disable-next-line
  }, [state.successFirmado]);

  useEffect(() => {
    if (isControlled) {
      onChange && onChange(state.listFirmantes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.listFirmantes]);

  return {
    /**Properties */
    loadingFirma: state.loadingFirma,
    keyFile: state.keyFile,
    certFile: state.certFile,
    tipo_firmante: state.tipo_firmante,
    showPassword: state.showPassword,
    successMessage,
    errorMessage: state.errorMessage,
    debounceSuccessMessage,
    showCertificadosGuardados: state.showCertificadosGuardados,
    statusFirma: state.listFirmantes,
    successFirmado: state.successFirmado,
    statusFirmaDigestiones: state.statusFirma,
    strCertificate: state.strCertificate,
    strPrivateKey: state.strPrivateKey,
    password: state.password,
    stepsFirmas: state.stepsFirmas,
    digestiones: state.digestiones,
    oPrivateKey: state.oPrivateKey,
    derResData: state.derResData,
    vectorResData: state.vectorResData,
    iteracionesEncontradas: state.iteracionesEncontradas,
    /**methods */
    addCerFile,
    addKeyFile,
    addTipoFirmante,
    cargarScripts,
    handleClickShowPassword,
    handleMouseDownPassword,
    initialFirmado,
    readCertificate,
    readPrivateKey,
    handleClickShowCerKeySave,
    startFinishFirmaAnticipada,
    handleAddCertSelected,
    handleAddKeySelected,
    validateKeyPairs,
    compareDateCertificate,
    startFirmado,
    startDigestionFiles,
    startGetVector,
    iniciaEncontrarIteracion,
    startGetFirmaArchivoExtendidos,
  };
};
